import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, CheckCircle, Star,
  Share2, TrendingUp, Search,
  Code, Cloud, Database, Smartphone,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PortfolioFooter from "./PortfolioFooter";

/* ─── DATA ─────────────────────────────────────────── */
const brandingServices = [
  { icon: <Star className="w-4 h-4" />,       name: "Brand Identity Design",    desc: "Logos, colour systems & brand guidelines" },
  { icon: <Share2 className="w-4 h-4" />,      name: "Social Media Marketing",   desc: "Content, community & paid social" },
  { icon: <TrendingUp className="w-4 h-4" />,  name: "Digital Marketing",        desc: "PPC, email & performance campaigns" },
  { icon: <Search className="w-4 h-4" />,      name: "SEO & Content Strategy",   desc: "Rank higher, reach wider" },
];

const softwareServices = [
  { icon: <Code className="w-4 h-4" />,        name: "Custom Software Dev",      desc: "Web, backend & SaaS platforms" },
  { icon: <Smartphone className="w-4 h-4" />,  name: "Mobile App Development",   desc: "iOS & Android native / hybrid" },
  { icon: <Cloud className="w-4 h-4" />,       name: "Cloud Architecture",       desc: "AWS, Azure & GCP deployments" },
  { icon: <Database className="w-4 h-4" />,    name: "Enterprise ERP & CRM",     desc: "Custom enterprise management systems" },
];

const stats = [
  { value: "100+", label: "Projects Delivered" },
  { value: "50+",  label: "Engineers" },
  { value: "40+",  label: "Brands Managed" },
  { value: "6+",   label: "Years Experience" },
];

const whyPoints = [
  "Full-service agency: branding AND engineering under one roof",
  "Dedicated team assigned to every project from day one",
  "Agile delivery with weekly demos and full transparency",
  "Performance-first — every project ships with measurable KPIs",
  "Post-launch support, monitoring & continuous improvement",
];

const processSteps = [
  {
    num: "01",
    title: "Discover",
    desc: "We deep-dive into your goals, market, and users to define a clear strategy before touching a pixel or writing a line of code.",
    image: "/images/photo-1531403009284-440f080d1e12.jpg",
  },
  {
    num: "02",
    title: "Design",
    desc: "Our creative and engineering teams co-design the solution — brand visuals, UX flows, and system architecture in parallel.",
    image: "/images/photo-1586717791821-3f44a563fa4c.jpg",
  },
  {
    num: "03",
    title: "Deliver",
    desc: "We ship in sprints, launch fast, and keep improving. From your first campaign to your 100k-user milestone — we're with you.",
    image: "/images/photo-1461749280684-dccba630e2f6.jpg",
  },
];

const testimonials = [
  {
    quote: "Winsil rebranded our entire identity and ran our social campaigns. Follower growth tripled in 3 months.",
    name: "Priya Menon",
    role: "CMO, StyleVault India",
    initials: "PM",
    color: "bg-purple-600",
    tag: "Branding",
    tagColor: "bg-purple-100 text-purple-700",
  },
  {
    quote: "Their agile process and transparent communication made us feel like genuine partners. The SaaS platform handles 10× our original load.",
    name: "Arjun Mehta",
    role: "CTO, FinVault Technologies",
    initials: "AM",
    color: "bg-orange-600",
    tag: "Software",
    tagColor: "bg-orange-100 text-orange-700",
  },
  {
    quote: "We hired Winsil for SEO content and ended up getting a full website rebuild too. Best decision we made this year.",
    name: "Sarah Okafor",
    role: "Founder, GreenHive Co.",
    initials: "SO",
    color: "bg-emerald-600",
    tag: "Branding + Software",
    tagColor: "bg-emerald-100 text-emerald-700",
  },
];

const mosaicImages = [
  { src: "/images/photo-1563986768494-4dee2763ff3f.jpg", alt: "Content creation",    label: "Content Creation" },
  { src: "/images/photo-1498050108023-c5249f4df085.jpg", alt: "Software development", label: "Software Dev" },
  { src: "/images/photo-1533750349088-cd871a92f312.jpg", alt: "Digital marketing",    label: "Digital Marketing" },
  { src: "/images/photo-1512941937669-90a1b58e7e9c.jpg", alt: "Mobile apps",         label: "Mobile Apps" },
  { src: "/images/photo-1542744173-8e7e53415bb0.jpg",    alt: "SEO & Content",       label: "SEO & Content" },
  { src: "/images/photo-1451187580459-43490279c0fa.jpg", alt: "Cloud architecture",  label: "Cloud Architecture" },
];

/* ─── COMPONENT ─────────────────────────────────────── */
const HomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="font-sans min-h-screen">
      <Helmet>
        <title>Winsil Evolution | Branding, Marketing &amp; Software Development</title>
        <meta name="description" content="Winsil Evolution delivers creative branding, digital marketing, and enterprise software development — two disciplines, one exceptional team based in New Delhi, India." />
        <meta name="keywords" content="software development, branding, digital marketing, SEO, UI UX design, social media marketing, mobile app development, cloud architecture, New Delhi" />
        <meta name="author" content="Winsil Evolution Pvt Ltd" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Winsil Evolution | We Brand. We Build." />
        <meta property="og:description" content="Creative branding and enterprise software — two disciplines, one team, infinite possibilities." />
        <meta property="og:site_name" content="Winsil Evolution" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Winsil Evolution | We Brand. We Build." />
        <meta name="twitter:description" content="Creative branding and enterprise software development from Winsil Evolution, New Delhi." />
      </Helmet>

      <PortfolioNavbar />

      {/* ── SECTION 1: HERO ── */}
      <div className="relative min-h-screen flex items-center overflow-hidden">
        {/* Split background */}
        <div className="absolute inset-0 grid grid-cols-2">
          <div className="relative bg-gradient-to-br from-purple-950 via-purple-900 to-pink-900 overflow-hidden">
            <img
              src="/images/photo-1561070791-2526d30994b5.jpg"
              alt="Branding design"
              className="absolute inset-0 w-full h-full object-cover opacity-15"
            />
          </div>
          <div className="relative bg-gray-950 overflow-hidden">
            <img
              src="/images/photo-1555066931-4365d14bab8c.jpg"
              alt="Software development"
              className="absolute inset-0 w-full h-full object-cover opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-gray-950/80 to-orange-950/40" />
          </div>
        </div>

        {/* Centre divider */}
        <div className="absolute inset-y-0 left-1/2 w-px bg-white/10 z-10 hidden lg:block" />

        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none z-10"
          style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
        />

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-16">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center px-5 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm gap-2">
              <span className="w-2 h-2 rounded-full bg-purple-400" />
              <span className="text-white/70 text-xs font-black uppercase tracking-[0.2em]">Winsil Evolution</span>
              <span className="w-2 h-2 rounded-full bg-orange-400" />
            </div>
          </div>

          {/* Headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-[1.05] text-center mb-5">
            We{" "}
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Brand.</span>
            {" "}We{" "}
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">Build.</span>
          </h1>
          <p className="text-white/50 text-lg sm:text-xl text-center max-w-2xl mx-auto leading-relaxed mb-10">
            Creative marketing &amp; enterprise software — two disciplines, one team, infinite possibilities.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button
              onClick={() => navigate("/branding")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30"
            >
              Explore Branding <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={() => navigate("/software-development")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-xl bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/30"
            >
              Explore Software <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>

          {/* Two hero preview panels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div
              onClick={() => navigate("/branding")}
              className="relative rounded-2xl overflow-hidden shadow-2xl group cursor-pointer h-56"
            >
              <img src="/images/photo-1579869847514-7c1a19d2d2ad.jpg" alt="Branding" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-950/85 via-purple-900/40 to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="text-xs font-black uppercase tracking-widest text-purple-300 mb-1">Service Area</p>
                <p className="text-2xl font-black text-white">Branding & Marketing</p>
              </div>
              <div className="absolute top-4 right-4 bg-purple-500/80 backdrop-blur-sm text-white text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full">
                Explore →
              </div>
            </div>

            <div
              onClick={() => navigate("/software-development")}
              className="relative rounded-2xl overflow-hidden shadow-2xl group cursor-pointer h-56"
            >
              <img src="/images/photo-1498050108023-c5249f4df085.jpg" alt="Software" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-950/85 via-gray-900/40 to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="text-xs font-black uppercase tracking-widest text-orange-400 mb-1">Service Area</p>
                <p className="text-2xl font-black text-white">Software Development</p>
              </div>
              <div className="absolute top-4 right-4 bg-orange-500/80 backdrop-blur-sm text-white text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-full">
                Explore →
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SECTION 2: SERVICE SELECTOR CARDS ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-center mb-3 text-gray-400">What We Do</p>
          <h2 className="text-4xl sm:text-5xl font-black text-gray-900 text-center mb-4">Two Powerful Services</h2>
          <p className="text-gray-500 text-base text-center max-w-2xl mx-auto mb-14">
            Whether you need to grow your brand or build your product — we have the expertise, the team, and the track record.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Branding card */}
            <div
              className="group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300 cursor-pointer min-h-[520px]"
              onClick={() => navigate("/branding")}
            >
              <div className="absolute inset-0">
                <img src="/images/photo-1557804506-669a67965ba0.jpg" alt="Branding work" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-br from-purple-950/95 via-purple-900/92 to-pink-900/88" />
              </div>
              <div className="relative z-10 p-10">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-500/30 border border-purple-400/40 mb-6">
                  <span className="text-purple-300 text-[10px] font-black uppercase tracking-[0.2em]">Branding & Marketing</span>
                </div>
                <h3 className="text-3xl font-black text-white mb-3">Grow Your Brand</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-8 max-w-sm">
                  Creative strategy, stunning design, and performance marketing that builds brands people love and competitors envy.
                </p>
                <ul className="space-y-4 mb-10">
                  {brandingServices.map((s) => (
                    <li key={s.name} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-purple-300 flex-shrink-0">
                        {s.icon}
                      </div>
                      <div>
                        <p className="text-white text-sm font-bold leading-snug">{s.name}</p>
                        <p className="text-white/40 text-xs">{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <button className="inline-flex items-center justify-center px-6 py-3 text-sm font-black uppercase tracking-wider rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 transition-all duration-300 hover:scale-105">
                  Explore Branding <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>

            {/* Software card */}
            <div
              className="group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300 cursor-pointer min-h-[520px]"
              onClick={() => navigate("/software-development")}
            >
              <div className="absolute inset-0">
                <img src="/images/photo-1522202176988-66273c2fd55f.jpg" alt="Software team" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-br from-gray-950/95 via-gray-900/92 to-orange-950/85" />
              </div>
              <div className="relative z-10 p-10">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/30 border border-orange-400/40 mb-6">
                  <span className="text-orange-300 text-[10px] font-black uppercase tracking-[0.2em]">Software Development</span>
                </div>
                <h3 className="text-3xl font-black text-white mb-3">Build Your Product</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-8 max-w-sm">
                  Full-stack engineering, cloud architecture, and enterprise software built to scale — from MVP to millions of users.
                </p>
                <ul className="space-y-4 mb-10">
                  {softwareServices.map((s) => (
                    <li key={s.name} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-orange-500/20 border border-orange-400/30 flex items-center justify-center text-orange-300 flex-shrink-0">
                        {s.icon}
                      </div>
                      <div>
                        <p className="text-white text-sm font-bold leading-snug">{s.name}</p>
                        <p className="text-white/40 text-xs">{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <button className="inline-flex items-center justify-center px-6 py-3 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105">
                  Explore Software <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SECTION 3: STATS STRIP ── */}
      <div className="bg-gray-900 border-y border-white/10 py-14 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
          {stats.map((s) => (
            <div key={s.label}>
              <p className="text-4xl sm:text-5xl font-black text-white mb-1">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/35">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── SECTION 4: IMAGE MOSAIC — OUR WORK ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-center mb-3 text-gray-400">Our Work</p>
          <h2 className="text-4xl font-black text-gray-900 text-center mb-4">Branding & Software in Action</h2>
          <p className="text-gray-500 text-base text-center max-w-xl mx-auto mb-12">
            A glimpse into the projects, campaigns, and platforms we've brought to life.
          </p>

          {/* 3-col top row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            {mosaicImages.slice(0, 3).map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* 3-col bottom row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {mosaicImages.slice(3).map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SECTION 5: WHY WINSIL ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 mb-3">Why Winsil</p>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 mb-6 leading-tight">
              One Team.<br />
              <span className="text-orange-500">Two Superpowers.</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              Most agencies do either branding or software. We do both — and the integration is where the magic happens.
              Your brand story flows seamlessly into your product experience.
            </p>
            <ul className="space-y-4 mb-10">
              {whyPoints.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate("/contact")}
                className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/20"
              >
                Work With Us <ArrowRight className="w-4 h-4 ml-2" />
              </button>
              <button
                onClick={() => navigate("/about-us")}
                className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-bold uppercase tracking-wider rounded-lg border-2 border-gray-300 text-gray-700 hover:border-orange-500 hover:text-orange-600 transition-all duration-300"
              >
                Meet the Team
              </button>
            </div>
          </div>

          {/* Right — multi-image collage */}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative rounded-2xl overflow-hidden shadow-xl group h-72 col-span-1">
              <img src="/images/photo-1552664730-d307ca884978.jpg" alt="Strategy planning" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-950/60 to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="text-white font-black text-sm uppercase tracking-wider">Strategy</span>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="relative rounded-2xl overflow-hidden shadow-xl group h-[120px]">
                <img src="/images/photo-1522071820081-009f0129c71c.jpg" alt="Team collaboration" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-950/60 to-transparent" />
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl group h-[120px]">
                <img src="/images/photo-1579532537598-459ecdaf39cc.jpg" alt="Branding work" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-950/60 to-transparent" />
              </div>
              <div className="bg-gray-900 rounded-2xl p-5 flex flex-col justify-center">
                <p className="text-3xl font-black text-orange-400">50+</p>
                <p className="text-white/50 text-xs font-bold uppercase tracking-wider mt-1">Expert Engineers</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── SECTION 6: HOW WE WORK ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-center mb-3 text-gray-400">Our Process</p>
          <h2 className="text-4xl font-black text-gray-900 text-center mb-4">How We Work</h2>
          <p className="text-gray-500 text-base text-center max-w-xl mx-auto mb-14">
            A proven 3-stage framework that applies to every branding campaign and every software project.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, i) => (
              <div key={step.num} className="relative group">
                <div className="relative rounded-2xl overflow-hidden shadow-lg h-52 mb-6">
                  <img src={step.image} alt={step.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/85 via-gray-900/30 to-transparent" />
                  <div className="absolute top-4 left-4 text-6xl font-black text-white/15 leading-none select-none">{step.num}</div>
                  <div className="absolute bottom-4 left-4">
                    <p className="text-white font-black text-xl">{step.title}</p>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{step.desc}</p>
                {i < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-24 -right-5 z-10 text-gray-300">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── EXTRA IMAGE ROW ── */}
      <div className="px-4 sm:px-6 lg:px-8 pb-20 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { src: "/images/photo-1551434678-e076c223a692.jpg",    alt: "Engineering team",  label: "Our Engineers" },
            { src: "/images/photo-1551836022-d5d88e9218df.jpg",    alt: "Analytics work",   label: "Data & Analytics" },
            { src: "/images/photo-1600880292203-757bb62b4baf.jpg", alt: "Creative team",    label: "Creative Team" },
          ].map((img, i) => (
            <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-52">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── SECTION 7: TESTIMONIALS ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-center mb-3 text-gray-400">Client Stories</p>
          <h2 className="text-4xl font-black text-gray-900 text-center mb-14">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-lg transition-shadow duration-300">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-orange-400 fill-orange-400" />
                  ))}
                </div>
                <span className={`inline-block px-2.5 py-0.5 text-xs font-black uppercase tracking-wider rounded-full mb-4 ${t.tagColor}`}>
                  {t.tag}
                </span>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${t.color} rounded-full flex items-center justify-center text-white text-sm font-black flex-shrink-0`}>
                    {t.initials}
                  </div>
                  <div>
                    <p className="text-gray-900 font-extrabold text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SECTION 8: DUAL CTA BANNER ── */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-2">
          <div className="bg-purple-950" />
          <div className="bg-gray-950" />
        </div>
        <img
          src="/images/photo-1540575467063-178a50c2df87.jpg"
          alt="Event background"
          className="absolute inset-0 w-full h-full object-cover opacity-10"
        />
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
        />
        <div className="absolute inset-y-0 left-1/2 w-px bg-white/10 hidden lg:block" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center text-white">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-white/40 mb-4">Ready to Start?</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-5">
            Grow your brand.<br />Build your product.
          </h2>
          <p className="text-white/50 text-lg mb-12 max-w-xl mx-auto">
            Tell us what you need and we'll have a proposal in your inbox within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/branding")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:opacity-90 transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30"
            >
              Start a Branding Project <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={() => navigate("/software-development")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-xl bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/30"
            >
              Start a Software Project <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default HomePage;
